import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TwoFactorSetup } from '../components/TwoFactorSetup';
import { TemplateLibraryView } from '../components/TemplateLibraryView';
import EmailAliasManager from '../components/agent/EmailAliasManager';
import { FaShieldAlt, FaUser, FaKey, FaCopy, FaCheck, FaTrash, FaFileAlt, FaRobot } from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface TwoFactorStatus {
  isEnabled: boolean;
  isSetup: boolean;
  backupCodesCount: number;
  lastUsed: Date | null;
}

interface AssistantMemory {
  id: string;
  content: string;
  createdAt: string;
}

const Settings: React.FC = () => {
  const { user, token, updateUser2FAStatus } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'templates' | 'assistant'>('profile');
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [memories, setMemories] = useState<AssistantMemory[] | null>(null);
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [deletingMemoryId, setDeletingMemoryId] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      fetchTwoFactorStatus();
    }
  }, [token]);

  useEffect(() => {
    if (token && activeTab === 'assistant' && memories === null) {
      fetchMemories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeTab]);

  const fetchMemories = async () => {
    setMemoriesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/assistant/memories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMemories(data.memories);
      }
    } catch (err) {
      console.error('Failed to fetch assistant memories:', err);
    } finally {
      setMemoriesLoading(false);
    }
  };

  const handleDeleteMemory = async (id: string) => {
    setDeletingMemoryId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/assistant/memories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setMemories((prev) => (prev ? prev.filter((m) => m.id !== id) : prev));
      }
    } catch (err) {
      console.error('Failed to delete assistant memory:', err);
    } finally {
      setDeletingMemoryId(null);
    }
  };

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const status = await response.json();
        setTwoFactorStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    const currentPassword = prompt('Please enter your current password to disable 2FA:');
    if (!currentPassword) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword }),
      });

      if (response.ok) {
        setSuccess('Two-factor authentication has been disabled');
        updateUser2FAStatus(false);
        await fetchTwoFactorStatus();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to disable 2FA');
      }
    } catch (error) {
      setError('Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!window.confirm('Are you sure you want to regenerate backup codes? Your existing backup codes will no longer work.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('Backup codes regenerated successfully');
        await fetchTwoFactorStatus();
        
        // Show the new backup codes
        const codesText = data.backupCodes.join('\n');
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head><title>New Backup Codes</title></head>
              <body style="font-family: monospace; padding: 20px;">
                <h2>Your New Backup Codes</h2>
                <p style="color: red;"><strong>Important:</strong> Save these codes in a secure location. Each code can only be used once.</p>
                <pre style="background: #f5f5f5; padding: 15px; border-radius: 5px;">${codesText}</pre>
                <button onclick="window.print()">Print Codes</button>
              </body>
            </html>
          `);
          newWindow.document.close();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to regenerate backup codes');
      }
    } catch (error) {
      setError('Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSetupComplete = () => {
    setShowTwoFactorSetup(false);
    setSuccess('Two-factor authentication has been enabled successfully');
    updateUser2FAStatus(true);
    fetchTwoFactorStatus();
  };

  const handleTwoFactorSetupCancel = () => {
    setShowTwoFactorSetup(false);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  if (showTwoFactorSetup) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <TwoFactorSetup
          onComplete={handleTwoFactorSetupComplete}
          onCancel={handleTwoFactorSetupCancel}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage your account settings and security preferences</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
        <nav className="flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('profile');
              clearMessages();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <FaUser className="inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => {
              setActiveTab('security');
              clearMessages();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'security'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <FaShieldAlt className="inline mr-2" />
            Security
          </button>
          <button
            onClick={() => {
              setActiveTab('templates');
              clearMessages();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <FaFileAlt className="inline mr-2" />
            Proposal Templates
          </button>
          <button
            onClick={() => {
              setActiveTab('assistant');
              clearMessages();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assistant'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <FaRobot className="inline mr-2" />
            Arie
          </button>
        </nav>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-800 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-800 dark:text-green-400 text-sm">{success}</p>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Profile Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={user?.firstName || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={user?.lastName || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role
                </label>
                <input
                  type="text"
                  value={user?.role || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white capitalize"
                />
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Profile information is managed by your organization administrator. Contact your admin to make changes.
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Email addresses you send from</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Mail sent or forwarded to the <a href="/agent?tab=settings" className="text-primary hover:underline">agent mailbox</a> is matched to you by the sender address.
              Your login ({user?.email}) always counts. If your mail goes out from a different address — another domain, an alias, a shared box — add it here so Arie knows it's you.
              We'll send a link to that address to confirm you own it.
            </p>
            <EmailAliasManager compact />
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* Password Section */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              <FaKey className="inline mr-2" />
              Password
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Manage your account password to keep your account secure.
            </p>
            <button
              onClick={() => window.location.href = '/change-password'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Change Password
            </button>
          </div>

          {/* Password + 2FA sections follow */}
          {/* Two-Factor Authentication Section */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              <FaShieldAlt className="inline mr-2" />
              Two-Factor Authentication
            </h3>
            
            {twoFactorStatus ? (
              <div>
                <div className="flex items-center mb-4">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    twoFactorStatus.isEnabled ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  <span className="text-gray-900 dark:text-white font-medium">
                    Status: {twoFactorStatus.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                {twoFactorStatus.isEnabled ? (
                  <div className="space-y-4">
                    <p className="text-gray-600 dark:text-gray-400">
                      Two-factor authentication is active on your account. You have {twoFactorStatus.backupCodesCount} backup codes remaining.
                    </p>
                    
                    {twoFactorStatus.lastUsed && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Last used: {new Date(twoFactorStatus.lastUsed).toLocaleString()}
                      </p>
                    )}

                    <div className="flex space-x-3">
                      <button
                        onClick={handleRegenerateBackupCodes}
                        disabled={loading}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                      >
                        {loading ? 'Regenerating...' : 'Regenerate Backup Codes'}
                      </button>
                      
                      <button
                        onClick={handleDisable2FA}
                        disabled={loading}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {loading ? 'Disabling...' : 'Disable 2FA'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Two-factor authentication adds an extra layer of security to your account. 
                      We recommend using Microsoft Authenticator for the best experience.
                    </p>
                    
                    <button
                      onClick={() => setShowTwoFactorSetup(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Enable Two-Factor Authentication
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Proposal Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          <TemplateLibraryView isAdmin={false} token={token} />
        </div>
      )}

      {/* Assistant Tab */}
      {activeTab === 'assistant' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              <FaRobot className="inline mr-2" />
              Arie's memory
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Durable preferences Arie has picked up from your conversations — the workflows you tend to run, naming conventions, that kind of thing. Only visible to you.
            </p>

            {memoriesLoading && memories === null && (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            )}

            {memories !== null && memories.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nothing remembered yet.</p>
            )}

            {memories !== null && memories.length > 0 && (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {memories.map((m) => (
                  <li key={m.id} className="flex items-start justify-between gap-3 py-3">
                    <span className="text-sm text-gray-800 dark:text-gray-200">{m.content}</span>
                    <button
                      onClick={() => handleDeleteMemory(m.id)}
                      disabled={deletingMemoryId === m.id}
                      className="shrink-0 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 transition-colors"
                      title="Forget this"
                    >
                      <FaTrash />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;